import { useCallback, useEffect, useState } from "react";
import axios from "../../api/axios";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { uploadFile, deleteFile } from "../../utils/upload";
import imageCompression from "browser-image-compression";
import {
  AddImageButton,
  CancelButton,
  DeleteButton,
  EditImageButton,
  SaveButton,
} from "../buttons/Buttons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const WEBSITE_URL = "/image_url";

export default function CarouselSuggEdit(props) {
  const galleryTitle = "gallery-" + props.title;
  const [images, setImages] = useState([]);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [lastIndex, setLastIndex] = useState(0);
  const [imageObjects, setImageObjects] = useState([]);
  const [status, setStatus] = useState("");
  const [index, setIndex] = useState(0);
  const maxImages = props.maxImages;
  const [uploadLegal, setUploadLegal] = useState(false);
  const [nofImagesInDB, setNofImagesInDB] = useState(-1);
  const [nofImagesToUpload, setNofImagesToUpload] = useState(0);

  const { t } = useTranslation();
  const { username } = useAuth();

  useEffect(() => {
    const intervalId = setInterval(() => {
      setIndex((index + 1) % imageObjects.length);
    }, 5000);
    return () => clearInterval(intervalId);
  }, [imageObjects.length, index]);

  const updateGallery = useCallback(async () => {
    const url = WEBSITE_URL + "/gallery";

    try {
      const response = await axios.get(url, {
        params: { title: galleryTitle, username },
      });

      if (response.data && response.data.length > 0) {
        const imgs = response.data.map((item, idx) => ({
          url: item.url,
          public_id: item.public_id,
          sortOrder: item.sortOrder ?? idx,
        }));

        setNofImagesInDB(imgs.length);
        setImageObjects(imgs.sort((a, b) => a.sortOrder - b.sortOrder));

        const nums = imgs
          .map((img) => Number(img.public_id.split("-").pop()))
          .filter((n) => !isNaN(n));

        setLastIndex(nums.length ? Math.max(...nums) : 0);
      } else {
        setImageObjects([]);
        setLastIndex(0);
      }
    } catch (err) {
      console.error(err);
    }
  }, [galleryTitle, username]);

  useEffect(() => {
    updateGallery();
  }, [updateGallery]);

  useEffect(() => {
    const uploadLegal = nofImagesInDB + nofImagesToUpload <= maxImages;
    setUploadLegal(uploadLegal);

    if (!uploadLegal) {
      setStatus(
        `Maximum (${maxImages}) reached. You can upload only ${
          maxImages - nofImagesInDB
        } more images.`
      );
    } else if (nofImagesInDB === maxImages) {
      setStatus(`Maximum (${maxImages}) number of images reached.`);
    } else {
      setStatus("Please choose a file to upload!");
    }
  }, [nofImagesInDB, nofImagesToUpload, maxImages]);

  const sensors = useSensors(useSensor(PointerSensor));

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      setImageObjects((items) => {
        const oldIndex = items.findIndex((i) => i.public_id === active.id);
        const newIndex = items.findIndex((i) => i.public_id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  function SortableGridItem({ image }) {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: image.public_id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: "grab",
      userSelect: "none",
      padding: "8px",
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <img className="gallery-image" src={image.url} alt="gallery" />
        <DeleteButton
          buttonText="Delete"
          id={image.public_id}
          onClick={image.onDelete}
        />
      </div>
    );
  }

  async function saveOrderToBackend() {
    setStatus("Saving order...");
    const order = imageObjects.map((img, index) => ({
      public_id: img.public_id,
      sortOrder: index,
    }));

    await axios.post("/image_url/reorder", {
      username,
      title: galleryTitle,
      order,
    });

    setStatus("Order saved!");
    updateGallery();
  }

  async function handleFileUpload(event) {
    const newImages = [...images];
    const files = event.target.files;

    const maxSizeMB = 0.3;
    const options = { maxSizeMB, maxWidthOrHeight: 1920 };

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const fileToLoad =
        file.size > maxSizeMB * 1024 * 1024
          ? await imageCompression(file, options)
          : file;

      newImages.push(fileToLoad);
    }

    setImages(newImages);
    setNofImagesToUpload(files.length);
  }

  const handleDelete = async (event) => {
    const public_id = event.target.id;

    await deleteFile(public_id);

    await axios.delete(WEBSITE_URL, {
      data: { username, title: galleryTitle, public_id },
    });

    updateGallery();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("Uploading...");

    const folder = props.username + "/" + galleryTitle;

    if (images.length < 1) {
      setStatus("Please choose at least one image!");
      return;
    }

    try {
      let currentIndex = lastIndex;

      for (let i = 0; i < images.length; i++) {
        currentIndex += 1;
        const newIndex = currentIndex;

        const data = await uploadFile(images[i], folder, `${props.title}-${newIndex}`);

        await axios.put("/image_url", {
          username: props.username,
          title: galleryTitle,
          url: data.secure_url,
          public_id: data.public_id,
        });
      }

      setStatus("Upload successful!");
      await updateGallery();
      setImages([]);
      setAdding(false);
    } catch (error) {
      console.error(error);
      setStatus("Error occurred during upload.");
    }
  };

  const handleOuterCancel = () => {
    setAdding(false);
    setEditing(false);
    setStatus("");
  };

  const handleInnerCancel = () => {
    setAdding(false);
    setStatus("");
    setNofImagesToUpload(0);
  };

  return (
    <div className="sugg-carousel">
      {editing ? (
        <div className="suggestions-carousel">
          {!adding && (
            <div>
              <AddImageButton
                buttonText={t("button.add")}
                onClick={() => {
                  setAdding(true);
                  setStatus("Please choose a file to upload!");
                }}
                disable={!uploadLegal}
              />
              <CancelButton
                buttonText={t("button.cancel")}
                onClick={handleOuterCancel}
              />
            </div>
          )}

          {adding && (
            <form onSubmit={handleSubmit}>
              <span className="img-upload-form">
                <input type="file" multiple onChange={handleFileUpload} />
              </span>
              <SaveButton
                buttonText={t("button.upload")}
                type="submit"
                enabled={uploadLegal}
              />
              <CancelButton buttonText={t("button.cancel")} onClick={handleInnerCancel} />
            </form>
          )}
        </div>
      ) : (
        <EditImageButton
          buttonText={t("button.edit_gallery")}
          onClick={() => setEditing(true)}
        />
      )}

      <div>
        {imageObjects.length > 0 && editing ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={imageObjects.map((img) => img.public_id)}
              strategy={verticalListSortingStrategy}
            >
              <div
                className="edit-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: "12px",
                  marginTop: "15px",
                }}
              >
                {imageObjects.map((image) => (
                  <SortableGridItem
                    key={image.public_id}
                    image={{ ...image, onDelete: handleDelete }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="image-sugg">
            {imageObjects.map((image, i) => (
              <div
                className={i === index ? "carousel-slide active" : "carousel-slide"}
                key={i}
              >
                <div className="image-sugg">
                  <img className="image-sugg" src={image.url} alt="gallery" />
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: "40px" }}>
          <SaveButton
            buttonText="Save Order"
            onClick={saveOrderToBackend}
            enabled={true}
          />
        </div>

        {status && <div className="status-info">{status}</div>}
      </div>
    </div>
  );
}
